import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/joy";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { contrastRatio, isValidHex } from "../../../theme/paletteGenerator";
import ColorInput from "./ColorInput";

interface ShadeRowProps {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ShadeRow: React.FC<ShadeRowProps> = ({ label, value, onChange, disabled, placeholder }) => {
  const valid = isValidHex(value);
  const contrastOnWhite = valid ? contrastRatio(value, "#ffffff") : 0;
  const contrastOnBlack = valid ? contrastRatio(value, "#000000") : 0;

  const handleCopy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      {/* Shade label */}
      <Typography
        level="body-xs"
        sx={{ minWidth: 32, color: "text.secondary", fontFamily: "monospace" }}
      >
        {label}
      </Typography>

      {/* Colour picker + hex input */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ColorInput value={value} onChange={onChange} disabled={disabled} placeholder={placeholder} />
      </Box>

      {/* Contrast badges */}
      {valid && (
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={`Contrast on white: ${contrastOnWhite.toFixed(1)}:1`} placement="top">
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: contrastOnWhite >= 4.5 ? "success.100" : contrastOnWhite >= 3 ? "warning.100" : "danger.100",
                color: contrastOnWhite >= 4.5 ? "success.700" : contrastOnWhite >= 3 ? "warning.700" : "danger.700",
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: 1.4,
                fontFamily: "monospace",
              }}
            >
              {contrastOnWhite.toFixed(1)}W
            </Box>
          </Tooltip>
          <Tooltip title={`Contrast on black: ${contrastOnBlack.toFixed(1)}:1`} placement="top">
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: contrastOnBlack >= 4.5 ? "success.100" : contrastOnBlack >= 3 ? "warning.100" : "danger.100",
                color: contrastOnBlack >= 4.5 ? "success.700" : contrastOnBlack >= 3 ? "warning.700" : "danger.700",
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: 1.4,
                fontFamily: "monospace",
              }}
            >
              {contrastOnBlack.toFixed(1)}B
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Copy */}
      <IconButton size="sm" variant="plain" onClick={handleCopy} disabled={disabled || !valid}>
        <CopyIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default ShadeRow;
